import { LitElement, html, css } from 'lit'
import { StoreMixin, store } from '../store.js'
import { navigate } from '../router.js'
import { api } from '../api.js'
import { openEventStream } from '../live.js'
import { openPicker } from '../wmj-flow.js'
import { toast } from './app-toast.js'
import { STATUSES, STATUS_LABEL } from '../status.js'
import './rich-editor.js'

// Ticket landing list (§7.3): filters, sort, agent views, pagination, and an
// inline "new ticket" composer.
class PageTickets extends StoreMixin(LitElement) {
  static properties = {
    params: { type: Array },
    _data: { state: true },
    _loading: { state: true },
    _showCreate: { state: true },
    _subject: { state: true },
    _busy: { state: true },
    _newClient: { state: true },
    _newJob: { state: true },
    _newAssignee: { state: true },
    _assigneeQuery: { state: true },
    _assigneeResults: { state: true },
    _search: { state: true },
    _searchResults: { state: true },
    _unassignedCount: { state: true }
  }

  static styles = css`
    :host { display: block; font-family: var(--font-sans); color: var(--eggplant-500); }

    /* Page head */
    .head { display: flex; align-items: flex-end; justify-content: space-between; gap: 28px;
            flex-wrap: wrap; margin-bottom: 32px; }
    .eyebrow { display: flex; flex-direction: column; gap: 8px; align-items: flex-start; }
    .eyebrow span { font-family: var(--font-eyebrow); font-weight: 700; font-size: 12px; line-height: 1.5;
                    letter-spacing: 0.12em; text-transform: uppercase; color: var(--eggplant-500); }
    .eyebrow i { width: 20px; height: 2px; background: var(--chartreuse-500); display: block; }
    h1 { margin: 10px 0 0; font-size: 52px; font-weight: 800; letter-spacing: -0.025em; line-height: 1; color: var(--eggplant-500); }
    .lede { margin: 12px 0 0; font-size: 16px; color: var(--neutral-600); }
    .lede strong { color: var(--eggplant-500); }
    .head-actions { display: flex; align-items: center; gap: 14px; }
    @media (max-width: 720px) { h1 { font-size: 40px; } .head-actions { width: 100%; } .search { flex: 1; } }

    /* Inputs / buttons */
    .search { width: 260px; height: 48px; box-sizing: border-box; font-family: var(--font-sans); font-size: 16px;
              padding: 12px 16px; background: var(--white); border: none; border-radius: 0; color: var(--eggplant-500);
              box-shadow: inset 0 0 0 2px var(--border-input); outline: none; }
    .search:focus { box-shadow: inset 0 0 0 2px var(--eggplant-500); }
    button.cta, button.outline {
      display: inline-flex; align-items: center; justify-content: center; gap: 8px; white-space: nowrap;
      font-family: var(--font-sans); font-weight: 600; font-size: 16px; line-height: 24px;
      padding: 12px 24px; border: none; border-radius: 0; cursor: pointer; transition: background-color .18s ease, color .18s ease;
    }
    button.cta { background: var(--chartreuse-500); color: var(--eggplant-500); }
    button.cta:hover { background: var(--chartreuse-600); }
    button.outline { background: transparent; color: var(--eggplant-500); box-shadow: inset 0 0 0 1px var(--eggplant-500); }
    button.outline:hover { background: var(--eggplant-500); color: var(--neutral-50); }
    button.cta:disabled, button.outline:disabled { opacity: 0.4; cursor: not-allowed; }
    .plus { font-size: 18px; line-height: 0; font-weight: 700; }

    /* Filter bar */
    .controls { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; margin-bottom: 24px; }
    .views { display: flex; gap: 8px; overflow-x: auto; padding-bottom: 2px; }
    .views button {
      display: inline-flex; align-items: center; gap: 6px; white-space: nowrap; cursor: pointer;
      font-family: var(--font-eyebrow); font-weight: 500; font-size: 12px; letter-spacing: 0.04em; text-transform: uppercase;
      padding: 6px 14px; border-radius: 999px; border: none; background: transparent; color: var(--eggplant-500);
      box-shadow: inset 0 0 0 1px var(--eggplant-200); transition: background .15s ease, color .15s ease;
    }
    .views button.active { background: var(--eggplant-500); color: var(--chartreuse-500); box-shadow: none; }
    .views .badge { display: inline-grid; place-items: center; min-width: 18px; height: 18px; box-sizing: border-box;
             padding: 0 5px; border-radius: 999px; font-size: 10px; font-weight: 800; line-height: 1;
             background: var(--eggplant-100); color: var(--eggplant-500); }
    .views button.active .badge { background: var(--chartreuse-500); color: var(--eggplant-500); }
    .filters { display: flex; gap: 16px; margin-left: auto; align-items: center; flex-wrap: wrap; }
    label.f { display: flex; align-items: center; gap: 8px; font-family: var(--font-eyebrow); font-size: 11px;
              font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--label-grey); }
    select { height: 44px; box-sizing: border-box; font-family: var(--font-sans); font-size: 15px; text-transform: none;
             letter-spacing: 0; padding: 0 34px 0 14px; background: var(--white); color: var(--eggplant-500); border: none;
             border-radius: 0; box-shadow: inset 0 0 0 1px var(--eggplant-500); cursor: pointer;
             appearance: none; -webkit-appearance: none;
             background-image: linear-gradient(45deg, transparent 50%, var(--eggplant-500) 50%), linear-gradient(135deg, var(--eggplant-500) 50%, transparent 50%);
             background-position: right 16px center, right 11px center; background-size: 5px 5px, 5px 5px; background-repeat: no-repeat; }

    /* List + pager */
    .list { display: grid; gap: 14px; }
    .empty, .loading { padding: 3rem; text-align: center; color: var(--neutral-600); background: var(--white);
                       border-radius: var(--radius-card); box-shadow: var(--shadow-row); }
    .pager { display: flex; justify-content: space-between; align-items: center; margin-top: 32px; flex-wrap: wrap; gap: 16px; }
    .pager .count { font-size: 13px; color: var(--neutral-600); }
    .tiles { display: inline-flex; gap: 8px; }
    .tiles button { min-width: 40px; height: 40px; padding: 0 10px; border: none; cursor: pointer;
             font-family: var(--font-sans); font-size: 15px; background: transparent; color: var(--eggplant-500);
             box-shadow: inset 0 0 0 1px var(--neutral-200); }
    .tiles button:disabled { opacity: 0.4; cursor: not-allowed; }
    .tiles .pageno { min-width: 40px; height: 40px; display: grid; place-items: center; font-weight: 600;
             background: var(--eggplant-500); color: var(--neutral-50); font-size: 15px; }

    /* Create form */
    .create { background: var(--white); border-radius: var(--radius-card); border-top: 3px solid var(--chartreuse-500);
              padding: 24px; margin-bottom: 24px; box-shadow: var(--shadow-card); }
    .create .grid { display: grid; grid-template-columns: minmax(0, 1fr); gap: 18px; margin-top: 18px; }  /* minmax: see page-ticket.js */
    .create input { width: 100%; box-sizing: border-box; height: 48px; font-family: var(--font-sans); font-size: 16px;
             padding: 12px 16px; background: var(--white); border: none; border-radius: 0; color: var(--eggplant-500);
             box-shadow: inset 0 0 0 2px var(--border-input); outline: none; }
    .create input:focus { box-shadow: inset 0 0 0 2px var(--eggplant-500); }
    .fields { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 16px; }
    .field .lbl { font-family: var(--font-eyebrow); font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase;
             color: var(--label-grey); margin-bottom: 8px; }
    .picker { cursor: pointer; box-sizing: border-box; height: 48px; display: flex; align-items: center;
             font-family: var(--font-sans); font-size: 15px; color: var(--eggplant-500); padding: 12px 16px;
             background: var(--white); border: none; box-shadow: inset 0 0 0 1px var(--eggplant-500); }
    .picker:hover { box-shadow: inset 0 0 0 2px var(--eggplant-500); }
    .picker.unset { color: #B4232B; box-shadow: inset 0 0 0 1px #B4232B; }
    .picker.muted { color: var(--neutral-500); }
    .results { box-shadow: inset 0 0 0 1px var(--border-input); margin-top: 4px; }
    .results div { padding: 8px 12px; cursor: pointer; font-size: 14px; }
    .results div:hover { background: var(--neutral-50); }
    .actions { display: flex; gap: 12px; padding-top: 2px; }
  `

  constructor() {
    super()
    this._data = null
    this._loading = true
    this._showCreate = false
    this._subject = ''
    this._busy = false
    this._search = ''
    this._searchResults = null
    this._unassignedCount = 0
    this._resetCreateFields()
  }

  // Full-text search (§7.9). Enter runs it; clearing the box restores the list.
  async _runSearch() {
    const q = this._search.trim()
    if (!q) { this._searchResults = null; return }
    try {
      const { tickets } = await api.get(`/tickets/search?q=${encodeURIComponent(q)}`)
      this._searchResults = tickets
    } catch (err) {
      toast(err.message || 'Search failed', 'error')
    }
  }

  _clearSearch() { this._search = ''; this._searchResults = null }

  // Reset the agent-only create-form selections (client/job/assignee).
  _resetCreateFields() {
    this._newClient = null
    this._newJob = null
    this._newAssignee = null
    this._assigneeQuery = ''
    this._assigneeResults = []
  }

  _toggleCreate() {
    this._showCreate = !this._showCreate
    if (!this._showCreate) { this._subject = ''; this._resetCreateFields() }
  }

  // Client/job use the same picker modal as the ticket sidebar (pick-only, saved on
  // submit); assignee is an agent typeahead.
  async _pickNewClient() {
    const c = await openPicker({ mode: 'company' })
    if (!c) return
    this._newClient = { clientId: c.clientId, name: c.name }
    this._newJob = null // job is scoped to the company
  }

  async _pickNewJob() {
    if (!this._newClient) { toast('Select a client first', 'info'); return }
    const j = await openPicker({ mode: 'job', clientId: this._newClient.clientId })
    if (!j) return
    this._newJob = { jobNumber: j.jobNumber, name: j.name }
  }

  async _searchNewAssignee(e) {
    this._assigneeQuery = e.target.value
    if (this._assigneeQuery.trim().length < 2) { this._assigneeResults = []; return }
    try { this._assigneeResults = await api.get(`/users?query=${encodeURIComponent(this._assigneeQuery.trim())}&role=agent`) }
    catch { this._assigneeResults = [] }
  }

  _selectNewAssignee(u) {
    this._newAssignee = { id: u.id, name: u.fullName || u.email }
    this._assigneeQuery = ''
    this._assigneeResults = []
  }

  connectedCallback() {
    super.connectedCallback()
    this._load()
    // Live updates (§8.5): agents' list stays current as tickets are created/changed.
    // The collection feed is agent-only; clients refresh their own list on navigation.
    if (this._isAgent) {
      this._stream = openEventStream('/tickets/stream', 'tickets-changed', () => this._reloadSoon())
      this._refreshUnassignedCount()
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this._stream?.close()
    clearTimeout(this._reloadTimer)
  }

  // Coalesce bursts into a single quiet refetch (no full-page loading flash).
  _reloadSoon() {
    clearTimeout(this._reloadTimer)
    this._reloadTimer = setTimeout(() => {
      this._load({ quiet: true })
      this._refreshUnassignedCount()
    }, 400)
  }

  // Badge on the Unassigned view button — the queue of active tickets no agent has
  // picked up yet. Independent of the current view/filters.
  async _refreshUnassignedCount() {
    try {
      const { total } = await api.get('/tickets?view=unassigned&pageSize=1')
      this._unassignedCount = total
    } catch { /* leave the last known count in place */ }
  }

  get _isAgent() { return this.appState.user?.role === 'agent' }

  async _load({ quiet = false } = {}) {
    if (!quiet) this._loading = true
    const f = this.appState.ticketFilters
    const params = new URLSearchParams()
    if (f.status) params.set('status', f.status)
    if (f.sort) params.set('sort', f.sort)
    if (f.dir) params.set('dir', f.dir)
    if (this._isAgent && f.view) params.set('view', f.view)
    if (f.page) params.set('page', f.page)
    try {
      this._data = await api.get(`/tickets?${params}`)
    } catch (err) {
      toast(err.message || 'Failed to load tickets', 'error')
    } finally {
      this._loading = false
    }
  }

  _setFilter(patch) {
    store.set({ ticketFilters: { ...this.appState.ticketFilters, page: 1, ...patch } })
    this._load()
  }

  _setPage(page) {
    store.set({ ticketFilters: { ...this.appState.ticketFilters, page } })
    this._load()
  }

  async _create(e) {
    e.preventDefault()
    const editor = this.renderRoot.querySelector('rich-editor')
    if (!this._subject.trim() || !editor || editor.isEmpty()) {
      toast('Add a subject and a description', 'info')
      return
    }
    if (this._isAgent && !this._newClient) {
      toast('Select a client', 'info')
      return
    }
    this._busy = true
    try {
      const { html: body } = editor.getContent()
      const payload = { subject: this._subject.trim(), body }
      // Agents set client (required) + optional job/owner; a client's own org is implicit.
      if (this._isAgent) {
        payload.workamajigCompanyId = this._newClient.clientId
        if (this._newJob) payload.workamajigJobNumber = this._newJob.jobNumber
        if (this._newAssignee) payload.assigneeId = this._newAssignee.id
      }
      const ticket = await api.post('/tickets', payload)
      toast(`Ticket #${ticket.number} created`, 'success')
      this._resetCreateFields()
      navigate(`/tickets/${ticket.number}`)
    } catch (err) {
      toast(err.message || 'Failed to create ticket', 'error')
    } finally {
      this._busy = false
    }
  }

  // Truthful summary line under the title, derived from the loaded page.
  _summary() {
    const d = this._data
    if (!d) return ''
    const noun = d.total === 1 ? 'ticket' : 'tickets'
    const parts = [html`<strong>${d.total} ${noun}</strong>`]
    if (this._isAgent && this._unassignedCount > 0) parts.push(html` · ${this._unassignedCount} unassigned`)
    return html`${parts}`
  }

  render() {
    return html`
      <div class="head">
        <div>
          <div class="eyebrow"><span>Support queue</span><i></i></div>
          <h1>Tickets</h1>
          <p class="lede">${this._searchResults !== null
            ? html`Search results for “${this._search.trim()}”`
            : this._summary()}</p>
        </div>
        <div class="head-actions">
          <input class="search" placeholder="Search tickets…" .value=${this._search}
            @input=${e => (this._search = e.target.value)}
            @keydown=${e => { if (e.key === 'Enter') this._runSearch(); if (e.key === 'Escape') this._clearSearch() }} />
          ${this._searchResults !== null
            ? html`<button class="outline" @click=${this._clearSearch}>Clear</button>`
            : html`<button class="cta" @click=${this._toggleCreate}>
                <span class="plus">${this._showCreate ? '✕' : '＋'}</span>${this._showCreate ? 'Close' : 'New ticket'}
              </button>`}
        </div>
      </div>

      ${this._searchResults !== null ? html`
        <div class="list">
          ${this._searchResults.length
            ? this._searchResults.map(t => html`<ticket-row .ticket=${t}></ticket-row>`)
            : html`<div class="empty">No tickets match “${this._search.trim()}”.</div>`}
        </div>
      ` : this._renderBody()}
    `
  }

  _renderBody() {
    const f = this.appState.ticketFilters
    return html`

      ${this._showCreate ? html`
        <form class="create" @submit=${this._create}>
          <div class="eyebrow"><span>New ticket</span><i></i></div>
          <div class="grid">
            <input placeholder="Summarize the issue in one line" required .value=${this._subject}
              @input=${e => (this._subject = e.target.value)} />
            <rich-editor .placeholder=${'Describe the issue, steps to reproduce, and the expected outcome…'}></rich-editor>
            ${this._isAgent ? html`
              <div class="fields">
                <div class="field">
                  <div class="lbl">Client</div>
                  <div class="picker ${this._newClient ? '' : 'unset'}" @click=${this._pickNewClient}>
                    ${this._newClient ? this._newClient.name : 'Select client…'}
                  </div>
                </div>
                <div class="field">
                  <div class="lbl">Workamajig job (optional)</div>
                  <div class="picker ${this._newJob ? '' : 'muted'}" @click=${this._pickNewJob}>
                    ${this._newJob ? `#${this._newJob.jobNumber}` : 'Select job…'}
                  </div>
                </div>
                <div class="field">
                  <div class="lbl">Assignee (optional)</div>
                  ${this._newAssignee
                    ? html`<div class="picker" @click=${() => (this._newAssignee = null)}>${this._newAssignee.name}</div>`
                    : html`
                      <input placeholder="Assign to agent…" .value=${this._assigneeQuery} @input=${this._searchNewAssignee} />
                      ${this._assigneeResults.length ? html`<div class="results">
                        ${this._assigneeResults.map(u => html`<div @click=${() => this._selectNewAssignee(u)}>${u.fullName || u.email}</div>`)}
                      </div>` : ''}`}
                </div>
              </div>` : ''}
            <div class="actions">
              <button class="cta" type="submit" ?disabled=${this._busy || (this._isAgent && !this._newClient)}>
                ${this._busy ? 'Creating…' : html`Create ticket <span class="plus">→</span>`}
              </button>
              <button class="outline" type="button" @click=${this._toggleCreate}>Cancel</button>
            </div>
          </div>
        </form>` : ''}

      <div class="controls">
        ${this._isAgent ? html`
          <span class="views">
            ${[['mine', 'Mine'], ['group', 'Team'], ['all', 'All'], ['unassigned', 'Unassigned']].map(([v, label]) => html`
              <button class=${(f.view || 'mine') === v ? 'active' : ''}
                @click=${() => this._setFilter({ view: v })}>${label}${v === 'unassigned' && this._unassignedCount > 0
                  ? html`<span class="badge">${this._unassignedCount}</span>` : ''}</button>`)}
          </span>` : ''}
        <div class="filters">
          <label class="f">Status
            <select @change=${e => this._setFilter({ status: e.target.value || null })}>
              <option value="" ?selected=${!f.status}>All Active</option>
              ${STATUSES.filter(s => s !== 'closed').map(s => html`<option value=${s} ?selected=${f.status === s}>${STATUS_LABEL[s]}</option>`)}
            </select>
          </label>
          <label class="f">Sort
            <select @change=${e => this._setFilter({ sort: e.target.value })}>
              <option value="last_activity" ?selected=${f.sort === 'last_activity'}>Recently updated</option>
              <option value="open_date" ?selected=${f.sort === 'open_date'}>Open date</option>
              <option value="client_name" ?selected=${f.sort === 'client_name'}>Client name</option>
            </select>
          </label>
        </div>
      </div>

      ${this._loading ? html`<div class="loading">Loading…</div>` : this._renderList()}
    `
  }

  _renderList() {
    const d = this._data
    if (!d || !d.tickets.length) return html`<div class="empty">No tickets found.</div>`
    const from = (d.page - 1) * (d.pageSize || d.tickets.length) + 1
    const to = from + d.tickets.length - 1
    return html`
      <div class="list">
        ${d.tickets.map(t => html`<ticket-row .ticket=${t}></ticket-row>`)}
      </div>
      <div class="pager">
        <span class="count">Showing ${from}–${to} of ${d.total} ${d.total === 1 ? 'ticket' : 'tickets'}</span>
        <div class="tiles">
          <button ?disabled=${d.page <= 1} @click=${() => this._setPage(d.page - 1)} aria-label="Previous page">‹</button>
          <span class="pageno">${d.page}</span>
          <button ?disabled=${d.page >= d.totalPages} @click=${() => this._setPage(d.page + 1)} aria-label="Next page">›</button>
        </div>
      </div>
    `
  }
}

customElements.define('page-tickets', PageTickets)
